/** One expected source's page — Batch 5, `docs/design/36-user-journeys.md`.
 *
 * > *"What do I need to send this week, and did it work?"*
 *
 * The Sources register answers that for thirty streams at once. This page answers it for one: what
 * the source is, where each of its windows stands, and what satisfied each window, with a link to
 * the reading.
 *
 * ## Borrowed, never recomputed
 *
 * The census row (`useCoverage`) is the source of every state on this page, found by id among the
 * window in force. The readings and documents are `useSourceLinks`, the same read the register's
 * "Show what arrived" uses. The owner control and the retirement control are the register's own
 * components, so a source cannot be owned or retired one way here and another there.
 *
 * ## Six states, shown apart
 *
 * Every window carries its own state word and glyph (`sourceModel.STATE`), and `data-state` for a
 * test to read. A window is never drawn as "ok / not ok": *missing* is somebody's to chase,
 * *unknown* is nobody's, and *declared nil* is a person's statement with a reason. Merging them is
 * how "we have no data" becomes "the emissions were zero".
 *
 * ## What satisfied a window
 *
 * The links read returns readings with their own dates, and a reading is placed under every window
 * it **overlaps**: a straddling reading appears under both, which is what overlap means, and the
 * allocation of its quantity between them is the inventory's business and not this page's.
 * Documents carry no period, so they are listed once for the whole period and not pinned to a
 * window. A window the census calls *submitted* that no listed reading overlaps says so, rather than
 * pretending a link exists.
 *
 * ## Actions
 *
 * Owning and retiring reuse the register's components as they are. **Declaring a nil, and taking
 * one back, is a form built into the register's row and is not a component**, so this page links to
 * the register for it instead of rebuilding it: a second implementation of a statement a filing
 * rests on is how two screens come to disagree.
 */

import { Link, useParams } from "@tanstack/react-router";

import { useMe } from "../api/auth";
import { useMembers } from "../api/queries";
import { useCoverage, useSourceLinks } from "../api/sources";
import type { SourceCoverage, SourceWindow } from "../api/sources";
import { Loaded, ReadFailed } from "../components/Loaded";
import { PageHeader } from "../components/PageHeader";
import { Empty, Panel } from "../components/Panel";
import { StatusPill } from "../components/StatusPill";
import { isTabularContentType } from "../lib/documents";
import { shortDate, statedQuantity, unitLabel } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { RequirePeriod } from "./NoPeriod";
import { RecordLink } from "./RecordLink";
import { ScreenFrame } from "./ScreenFrame";
import { SourceOwner } from "./Sources";
import { SourceRetirement } from "./SourceRetirement";
import { keepSearch } from "./search";
import { LIFE_BASIS, STATE, nameOf, overlapping, period } from "./sourceModel";
import { StateGlyph, SupplyLine } from "./sourceParts";
import { StateTally } from "./StateTally";
import type { Screen, Window as ScreenWindow } from "./useScreen";
import styles from "./DetailPage.module.css";
import own from "./Sources.module.css";

function sentenceCase(text: string): string {
  const spaced = text.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function WindowCell({
  source,
  window,
  links,
}: {
  source: SourceCoverage;
  window: SourceWindow;
  links: ReturnType<typeof useSourceLinks>;
}) {
  const meaning = STATE[window.state];
  const readings = links.data ? overlapping(links.data.activities, window) : [];
  return (
    <li
      className={styles.window}
      data-testid="source-window"
      data-state={window.state}
      data-period-start={window.period_start}
    >
      <span className={styles.windowHead}>
        <StateGlyph state={window.state} />
        {period(window, source.cadence)} · {meaning?.short ?? window.state.replace(/_/g, " ")}
      </span>
      <span className={styles.windowDates}>
        {shortDate(window.period_start)} – {shortDate(window.period_end)}
      </span>
      {window.state === "declared_nil" ? (
        <span data-testid="source-window-nil">
          {window.nil_reason ? `Confirmed none: “${window.nil_reason}”` : "Confirmed none, with no reason on this read."}
        </span>
      ) : null}
      {window.state === "submitted" ? (
        links.error ? (
          <span>What arrived could not be read.</span>
        ) : links.isPending ? (
          <span>Reading what arrived…</span>
        ) : readings.length === 0 ? (
          <span data-testid="source-window-unlinked">
            Counted as submitted, but no reading in this source&rsquo;s trail overlaps it.
          </span>
        ) : (
          <ul className={styles.windowList}>
            {readings.map((reading) => (
              <li key={reading.activity_id}>
                <Link
                  className={styles.link}
                  to="/records/$recordId"
                  params={{ recordId: reading.activity_id }}
                  search={keepSearch}
                  data-testid="source-window-reading"
                >
                  {statedQuantity(reading.quantity)} {unitLabel(reading.unit)}
                </Link>{" "}
                <span className={styles.rowMeta}>
                  {shortDate(reading.period_start)} – {shortDate(reading.period_end)}
                </span>
                {reading.is_effective ? null : (
                  <span className={styles.superseded}> · superseded by a correction</span>
                )}
              </li>
            ))}
          </ul>
        )
      ) : null}
      {window.state === "missing" ? (
        <span data-testid="source-window-missing">{meaning?.long}. Nothing has been recorded for it.</span>
      ) : null}
      {window.state === "pending" || window.state === "unknown" || window.state === "not_expected" ? (
        <span>{sentenceCase(meaning?.long ?? "")}.</span>
      ) : null}
    </li>
  );
}

function Documents({ links }: { links: ReturnType<typeof useSourceLinks> }) {
  return (
    <Panel
      title="Documents"
      sub="Cited by this source's readings in this period; a document carries no period of its own"
      column="1 / 13"
    >
      <Loaded
        query={links}
        of="What arrived for this source"
        empty={<Empty>Nothing has been recorded against this source.</Empty>}
      >
        {(data) =>
          data.documents.length === 0 ? (
            <Empty>No document is cited by this source&rsquo;s readings in this period.</Empty>
          ) : (
            <>
              <p className={styles.note}>{data.sentence}</p>
              <ul className={styles.list} data-testid="source-documents">
                {data.documents.map((document) => (
                  <li key={document.document_id} className={styles.row}>
                    <span className={styles.rowMain}>
                      {/* A spreadsheet is imported rather than read, and goes to the other screen:
                          the register's own rule (`/evidence/$documentId` has no extraction for a
                          file of rows). */}
                      {isTabularContentType(document.content_type) ? (
                        <Link
                          className={styles.link}
                          to="/evidence/$documentId/import"
                          params={{ documentId: document.document_id }}
                          search={keepSearch}
                        >
                          {document.original_filename ?? "Spreadsheet"}
                        </Link>
                      ) : (
                        <Link
                          className={styles.link}
                          to="/evidence/$documentId"
                          params={{ documentId: document.document_id }}
                          search={keepSearch}
                        >
                          {document.original_filename ?? "Document"}
                        </Link>
                      )}
                      <span className={styles.rowMeta}>
                        {document.state.replace(/_/g, " ")}
                        {document.cites > 1 ? ` · cited by ${document.cites} readings` : ""}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )
        }
      </Loaded>
    </Panel>
  );
}

function SourceBody({
  source,
  current,
  retryCensus,
}: {
  source: SourceCoverage;
  current: ScreenWindow;
  retryCensus: () => void;
}) {
  const links = useSourceLinks(source.source_id, current.period_start, current.period_end);
  const { data: me } = useMe();
  const mayDeclare = me?.role === "approver" || me?.role === "admin";
  const isAdmin = me?.role === "admin";
  // The roster is Admin-only on the server: asked for only by an Admin, so a Contributor's page
  // load never collects a 403 (`Sources.tsx` says why in full).
  const roster = useMembers(isAdmin);
  const people = roster.data;
  const mayPickOwner = Boolean(mayDeclare && isAdmin && !roster.error && people);
  const name = nameOf(source);

  return (
    <>
      <PageHeader
        title={name}
        subtitle={`${source.facility_name} · ${sentenceCase(source.category)} · ${source.cadence.replace(/_/g, "-")}`}
        actions={
          <Link className={styles.link} to="/sources" search={keepSearch}>
            Back to Missing ›
          </Link>
        }
      />
      <div className={styles.statusRow} data-testid="source-status">
        {source.is_retired ? (
          <StatusPill tone="warn" testId="source-retired-pill">
            Retired {shortDate(source.expected_to)}
          </StatusPill>
        ) : (
          <StatusPill tone="ok" testId="source-retired-pill">
            Expected
          </StatusPill>
        )}
        {source.owner_user_id === null ? (
          <StatusPill tone="warn" testId="source-owner-pill">
            No operating owner
          </StatusPill>
        ) : (
          <StatusPill tone="ok" testId="source-owner-pill">
            Has an operating owner
          </StatusPill>
        )}
        <StateTally windows={source.windows} testId="source-tally" />
      </div>

      <Panel title="What this source is" sub="From the expected bills" column="1 / 13">
        <dl className={styles.kv} data-testid="source-facts">
          <dt>Category</dt>
          <dd>{sentenceCase(source.category)}</dd>
          <dt>Carrier</dt>
          <dd>{sentenceCase(source.fuel_or_carrier)}</dd>
          <dt>Site</dt>
          <dd>
            <Link
              className={styles.link}
              to="/organisation/sites/$facilityId"
              params={{ facilityId: source.facility_id }}
              search={keepSearch}
              data-testid="source-site-link"
            >
              {source.facility_name}
            </Link>
          </dd>
          <dt>Meter</dt>
          <dd>{source.meter_label ?? "Not tied to a meter"}</dd>
          <dt>Cadence</dt>
          <dd>{source.cadence.replace(/_/g, "-")}</dd>
          <dt>Expected from</dt>
          <dd>{shortDate(source.expected_from)}</dd>
          <dt>Expected until</dt>
          <dd data-testid="source-life">
            {source.life_to
              ? `${shortDate(source.life_to)} — ${LIFE_BASIS[source.life_to_basis ?? ""]?.said ?? source.life_to_basis}`
              : "No end date"}
            {source.retired_reason ? `. ${source.retired_reason}` : ""}
          </dd>
          <dt>Billed by</dt>
          <dd>
            <SupplyLine source={source} />
          </dd>
          <dt>Operating owner</dt>
          <dd>
            <SourceOwner
              source={source}
              people={people}
              mayAssign={mayDeclare}
              mayPick={mayPickOwner}
              meId={me?.user_id}
              from={current.period_start}
              to={current.period_end}
              onReload={retryCensus}
            />
          </dd>
        </dl>
      </Panel>

      <Panel
        title="Windows"
        sub={`${source.windows.length} in ${shortDate(current.period_start)} – ${shortDate(current.period_end)}; each stays in its own state`}
        column="1 / 13"
      >
        {source.windows.length === 0 ? (
          <Empty>This source has no period in the reporting period.</Empty>
        ) : (
          <ol className={styles.windows} data-testid="source-windows">
            {source.windows.map((window) => (
              <WindowCell key={window.period_start} source={source} window={window} links={links} />
            ))}
          </ol>
        )}
      </Panel>

      <Documents links={links} />

      <Panel title="Actions" sub="What can be done to this source" column="1 / 13">
        <SourceRetirement
          source={source}
          name={name}
          from={current.period_start}
          to={current.period_end}
          mayRetire={me ? mayDeclare : undefined}
          onReload={retryCensus}
        >
          <RecordLink recordId={source.source_id} kind="expected_source" subject={name} />
        </SourceRetirement>
        {me && !mayDeclare ? (
          <p className={styles.note} data-testid="source-actions-who">
            An <strong>Approver</strong> or an <strong>Admin</strong> retires a source and answers a
            missing bill; you can read everything here.
          </p>
        ) : null}
        <p className={`${own.formNote} ${styles.note}`} data-testid="source-nil-where">
          A missing bill is answered, and an answer taken back, on the{" "}
          <Link className={styles.link} to="/sources" search={keepSearch}>
            Processing &gt; Missing ›
          </Link>{" "}
          — a filing rests on it, so there is one place.
        </p>
      </Panel>
    </>
  );
}

function SourceDetailBody({ screen, current }: { screen: Screen; current: ScreenWindow }) {
  const { sourceId } = useParams({ from: "/sources/$sourceId" });
  const census = useCoverage(current.period_start, current.period_end);
  const source = census.data?.sources.find((entry) => entry.source_id === sourceId);
  return (
    <ScreenFrame screen={screen} title="Source" crumb={source ? nameOf(source) : "one expected source"} bodyScrolls>
      {census.error ? (
        <Panel title="Source" column="1 / 13">
          <ReadFailed
            of="The expected bills"
            error={census.error}
            onRetry={() => void census.refetch()}
          />
        </Panel>
      ) : census.isPending ? (
        <Panel title="Source" column="1 / 13">
          <Empty>Loading the expected bills…</Empty>
        </Panel>
      ) : source ? (
        <SourceBody source={source} current={current} retryCensus={() => void census.refetch()} />
      ) : (
        <Panel title="Source" column="1 / 13">
          {/* The same sentence for an id that does not exist and one this window's census does not
              list: telling them apart would say whether it exists. */}
          <p className={styles.note} data-testid="source-none">
            This period&rsquo;s expected bills do not list that source. It may belong to another
            period, which the period selector reaches, or it may not exist.
          </p>
          <Link className={styles.link} to="/sources" search={keepSearch}>
            Back to Missing ›
          </Link>
        </Panel>
      )}
    </ScreenFrame>
  );
}

export function SourceDetailScreen() {
  return (
    <RequireSession>
      <RequirePeriod title="Source" crumb="one expected source">
        {(screen, current) => <SourceDetailBody screen={screen} current={current} />}
      </RequirePeriod>
    </RequireSession>
  );
}
